import { ReactNode, useEffect } from 'react';
import { X } from 'lucide-react';
import BaseCard from '../cards/BaseCard';

interface LightModalProps {
  isOpen: boolean;
  title: ReactNode;
  children: ReactNode;
  onClose: () => void;
  className?: string;
  showCloseButton?: boolean;
  closeOnBackdropClick?: boolean;
  closeOnEscape?: boolean;
}

export function LightModal({
  isOpen,
  title,
  children,
  onClose,
  className = 'max-w-4xl',
  showCloseButton = true,
  closeOnBackdropClick = true,
  closeOnEscape = true,
}: LightModalProps) {
  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (closeOnBackdropClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  useEffect(() => {
    if (!closeOnEscape) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose, closeOnEscape]);

  return (
    <>
      <style>{`
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: #9ca3af #f3f4f6;
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 12px;
          opacity: 1;
          visibility: visible;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f3f4f6;
          border-radius: 9999px;
          opacity: 1;
          visibility: visible;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #9ca3af;
          border-radius: 9999px;
          opacity: 1;
          visibility: visible;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #6b7280;
        }
        .custom-scrollbar::-webkit-scrollbar-corner {
          background: transparent;
        }
      `}</style>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
        onClick={handleBackdropClick}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'light-modal-title' : undefined}
      >
        <div className={`max-h-[90vh] w-full ${className}`} onClick={e => e.stopPropagation()}>
          <BaseCard
            title={title as string}
            unpadded
            headerRightComponent={
              showCloseButton && (
                <button
                  onClick={onClose}
                  className="rounded-full p-2 text-neutral-100 transition-colors hover:bg-white/20"
                  aria-label="Close modal"
                >
                  <X className="h-5 w-5" />
                </button>
              )
            }
            className="max-h-[90vh] overflow-hidden"
          >
            <div
              className="custom-scrollbar max-h-[calc(90vh-80px)] overflow-y-auto scroll-smooth px-8 pb-6 pt-4"
              style={{
                scrollbarWidth: 'thin',
                scrollbarColor: '#9ca3af #f3f4f6',
              }}
            >
              {children}
            </div>
          </BaseCard>
        </div>
      </div>
    </>
  );
}
